<template>
    <div>
        <div v-if="loading">
            <div class="card">
                <div class="card-body d-flex justify-content-center">
                    <b-spinner variant="primary" label="Spinning"></b-spinner>
                </div>
            </div>
        </div>
        <div
            class="card mb-2 pointer hover"
            :class="{ 'border-primary my-3': currentLab == index }"
            v-for="(lab, index) in labs"
            :key="index"
            @click="currentLab = currentLab !== index ? index : -1"
        >
            <div class="p-3">
                <div class="row align-items-center">
                    <div class="col">
                        <div class="row">
                            <div class="col-12 col-lg-3 mb-3 mb-lg-0 d-flex align-items-center">
                                <div class="row">
                                    <div class="col-auto text-muted small d-flex align-items-center">
                                        <status-badge
                                            :icon-only="true"
                                            :status="lab.status"
                                        ></status-badge>
                                    </div>
                                    <div class="col">
                                        <div>{{ $t('Date created') }}: {{ lab.createdAt | moment('calendar') }}</div>
                                        <span class="small text-muted">{{ $t('Completion date') }}: {{ lab.dueDate | moment('calendar') }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-lg">
                                <div class="row">
                                    <div class="col-12 col-md-6 col-xl">
                                        <template v-if="lab.customer && !hideCustomer">
                                            <label class="text-muted">{{ $t('Customer') }}</label>
                                            <customer-card :customer="lab.customer"></customer-card>
                                        </template>
                                    </div>
                                    <div class="col-12 col-md-6 col-xl">
                                        <template v-if="lab.technician">
                                            <label class="text-muted">{{ $t('Technician') }}</label>
                                            <technician-card :technician="lab.technician"></technician-card>
                                        </template>
                                    </div>
                                    <div class="col-12 col-lg mt-3 mt-xl-0 d-flex align-items-center" v-if="lab.teeth">
                                        <template v-for="(group, groupIndex) in lab.teeth">
                                            <span
                                                v-if="group.treatment && groupIndex != 1"
                                                :key="groupIndex"
                                            >
                                                <div>{{ $t("Kind of work") }}</div>
                                                <span class="small text-muted">
                                                    <strong
                                                    >{{ group.treatment.code }}-
                                                    </strong>
                                                    <em>{{ group.treatment.label }}</em>
                                                </span>
                                            </span>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-auto">
                        <button class="btn" v-if="currentLab === index">
                            <i class="fe fe-chevron-down"></i>
                        </button>
                        <button class="btn" v-else>
                            <i class="fe fe-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>
            <div class="p-3" v-if="currentLab === index">
                <hr class="mt-2" />
                <div class="row" @click.stop>
                    <div class="col">
                        <div
                            v-if="lab.forSending.length"
                            class="blockquote mb-2"
                        >
                            <p class="mb-1">
                                <i class="fe fe-message-circle mr-1"></i>
                                {{ $t("For sending") }}
                            </p>
                            <footer
                                class="blockquote-footer d-inline-block mr-3"
                                v-for="(sendingItem, index) in lab.forSending"
                                :key="index"
                            >
                                <cite
                                    >{{ sendingItem.text }}
                                    {{ sendingItem.qty }}</cite
                                >
                            </footer>
                        </div>
                    </div>
                    <div class="col" v-if="lab.cancelExplanation">
                        <span class="text-muted"
                            >{{ $t("Reason for cancelling") }}:</span
                        >
                        {{ lab.cancelExplanation }}
                    </div>
                    <div class="col-auto">
                        <confirmable-button
                            class="d-inline-block mr-3"
                            v-if="
                                isDueDate(lab.dueDate) &&
                                    lab.status !== 'cancelled' &&
                                    lab.status !== 'completed'
                            "
                            button-class="btn mr-3 btn-outline-success"
                            v-b-tooltip
                            @confirm="() => setLabStatus(lab, 'completed')"
                            :title="$t('Accept')"
                        >
                            <i class="fe fe-check-circle"></i>
                        </confirmable-button>
                        <button
                            v-if="
                                !isDueDate(lab.dueDate) &&
                                    lab.status !== 'cancelled' &&
                                    lab.status !== 'completed'
                            "
                            class="btn mr-3 btn-outline-danger"
                            v-b-tooltip
                            :title="$t('Cancel')"
                            @click="openRemoveModal(lab)"
                        >
                            <i class="fe fe-x-circle"></i>
                        </button>
                        <button
                            v-if="
                                lab.status !== 'cancelled' &&
                                    lab.status !== 'completed'
                            "
                            class="btn mr-3 btn-outline-primary"
                            v-b-tooltip
                            :title="$t('Edit')"
                            @click="openLabModal(lab)"
                        >
                            <i class="fe fe-edit"></i>
                        </button>
                        <button
                            class="btn mr-3 btn-outline-primary"
                            v-if="
                                lab.status !== 'cancelled' &&
                                    lab.status !== 'completed'
                            "
                            v-b-tooltip
                            :title="$t('Create entry')"
                            @click="openAppointmentModalLab(lab)"
                        >
                            <i class="fe fe-calendar"></i>
                        </button>
                        <button
                            class="btn mr-3 btn-outline-primary"
                            v-b-tooltip
                            :title="$t('Print')"
                            @click="openPrintModal(lab)"
                        >
                            <i class="fe fe-printer"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { modalMixins } from "@/mixins/modal.js";
import { mapActions } from "vuex";
import ConfirmableButton from "@/components/ConfirmableButton";
import StatusBadge from "@/Pages/Company/Tasks/StatusBadge";
import LabModal from "@/Pages/Company/Labs/LabModal";
import PrintLabModal from "@/Pages/Company/Labs/PrintLabModal";
import RemoveModal from "@/Pages/Company/Labs/RemoveModal";
import CustomerCard from "@/components/CustomerCard.vue";
import User from "@/components/User.vue";
import TechnicianCard from "@/components/TechnicianCard.vue";

export default {
    mixins: [modalMixins],
    props: {
        labs: {
            type: Array,
            required: true
        },
        hideCustomer: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    components: {
        User,
        ConfirmableButton,
        StatusBadge,
        LabModal,
        PrintLabModal,
        RemoveModal,
        CustomerCard,
        TechnicianCard,
    },

    data() {
        return {
            data: {},
            currentLab: 0
        };
    },
    methods: {
        ...mapActions("labs", { patchLab: "patch" }),

        setLabStatus(lab, status) {
            this.patchLab([lab._id, { status: status }]);
            this.currentLab = 0;
        },

        isDueDate(dueDate) {
            return this.$moment(dueDate) <= this.$moment();
        },

        openRemoveModal(lab) {
            this.openModal({ id: "LabRemoveModal" });
            const { Lab } = this.$FeathersVuex;
            new Lab({
                model: lab,
                _id: "temp"
            });
        },

        openPrintModal(lab) {
            this.openModal({ id: "PrintLabModal" });
            const { Lab } = this.$FeathersVuex;
            new Lab({
                model: lab,
                _id: "temp"
            });
        },

        openLabModal(lab) {
            this.openModal({ id: "LabModal" });
            const { Lab } = this.$FeathersVuex;
            new Lab({
                model: lab,
                _id: "temp"
            });
        },

        openAppointmentModalLab(lab) {
            let data = {};
            if (lab.customerId) data.customerId = lab.customerId;
            data.startAt =
                (lab.fittings && lab.fittings.length
                    ? lab.fittings[0].date
                    : null) || lab.dueDate;
            data.services = [{}];
            this.openModal({ id: "AppointmentFastModal" });
            const { Appointment } = this.$FeathersVuex;

            new Appointment({
                model: data,
                _id: "temp"
            });
        }
    }
};
</script>
